<template>
  <div>
    <!-- Header con filtros -->
    <v-card class="mb-3" elevation="2">
      <v-card-title class="py-2">
        <v-row align="center" dense>
          <v-col cols="12" md="3" sm="4">
            <v-select
              :items="servicios"
              item-text="nomServicio"
              item-value="servicio"
              label="Servicio / Especialidad"
              prepend-icon="mdi-stethoscope"
              v-model="servicioSeleccionado"
              @change="cargarProfesionalesDelServicio"
              dense
              hide-details
              no-data-text="No hay Servicios configurados"
            ></v-select>
          </v-col>
          <v-col cols="12" md="5" sm="5" class="d-flex align-center">
            <v-btn icon @click="semanaAnterior" :disabled="cargando || esSemanaActual">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="text-subtitle-2 mx-2 font-weight-medium">{{ rangoSemana }}</span>
            <v-btn icon @click="semanaSiguiente" :disabled="cargando">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn small outlined color="primary" @click="irAHoy" class="ml-2">Hoy</v-btn>
          </v-col>
          <v-col cols="12" md="4" sm="3" class="text-right">
            <v-progress-circular
              v-if="cargando"
              indeterminate
              size="20"
              width="2"
              color="primary"
            ></v-progress-circular>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-subtitle class="pb-2 pt-0">
        <div class="mb-1">
          <v-icon x-small color="orange darken-2" class="mr-1">mdi-lightning-bolt</v-icon>
          <span class="text-caption font-weight-bold orange--text text--darken-2">Vista rápida para balanceo de turnos por servicio. Solo alta de turnos.</span>
        </div>
        <v-icon x-small color="green darken-1" class="mr-1">mdi-circle</v-icon>
        <span class="text-caption mr-3">Disponible (click = Nuevo Turno)</span>
        <v-icon x-small color="red darken-1" class="mr-1">mdi-circle</v-icon>
        <span class="text-caption mr-3">Ocupado (click = Entre Turno)</span>
        <v-icon x-small color="purple darken-1" class="mr-1">mdi-circle</v-icon>
        <span class="text-caption mr-3">Entre Turno</span>
        <v-icon x-small color="grey" class="mr-1">mdi-circle</v-icon>
        <span class="text-caption">Cancelado</span>
      </v-card-subtitle>
    </v-card>

    <!-- Estado vacío -->
    <div v-if="servicioSeleccionado === null" class="text-center mt-8">
      <v-icon size="64" color="grey lighten-1">mdi-stethoscope</v-icon>
      <p class="text-h6 grey--text mt-4">Seleccione un servicio para ver las agendas</p>
    </div>

    <div v-else-if="profesionalesServicio.length === 0 && !cargando" class="text-center mt-8">
      <v-icon size="64" color="grey lighten-1">mdi-account-off</v-icon>
      <p class="text-h6 grey--text mt-4">No hay profesionales configurados para este servicio</p>
    </div>

    <!-- Cards por día -->
    <v-row v-else-if="servicioSeleccionado !== null" dense>
      <v-col
        v-for="dia in diasSemana"
        :key="dia.diaIdx"
        cols="12"
        md="6"
        lg="4"
        xl="3"
      >
        <!-- Día pasado: placeholder vacío -->
        <v-card
          v-if="dia.esPasado"
          outlined
          class="dia-card dia-pasado"
        >
          <v-card-title class="dia-header py-2 px-3">
            <v-icon small class="mr-2" color="grey lighten-1">mdi-calendar</v-icon>
            <span class="text-subtitle-1 font-weight-bold grey--text text--lighten-1">{{ dia.nombreCorto }}</span>
            <span class="text-subtitle-2 ml-2 grey--text text--lighten-1">{{ dia.fechaStr }}</span>
          </v-card-title>
        </v-card>

        <!-- Día actual o futuro: card con datos -->
        <v-card
          v-else
          outlined
          class="dia-card"
          :class="{ 'dia-hoy': esHoy(dia.fecha) }"
        >
          <v-card-title class="dia-header py-2 px-3">
            <v-icon small class="mr-2" :color="esHoy(dia.fecha) ? 'primary' : 'grey'">mdi-calendar</v-icon>
            <span class="text-subtitle-1 font-weight-bold">{{ dia.nombreCorto }}</span>
            <span class="text-subtitle-2 ml-2 grey--text text--darken-1">{{ dia.fechaStr }}</span>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pa-2">
            <!-- Profesionales que atienden ese día -->
            <div
              v-for="prof in profesionalesConTurnosElDia(dia.diaIdx)"
              :key="prof.idProfesional"
              class="prof-section mb-3"
            >
              <div class="prof-nombre mb-1">
                <v-icon x-small color="primary" class="mr-1">mdi-account</v-icon>
                <span class="text-caption font-weight-bold primary--text">{{ prof.nombreProfesional }}</span>
              </div>
              <div class="chips-container">
                <v-chip
                  v-for="slot in getTurnosProfDia(prof.idProfesional, dia.diaIdx)"
                  :key="slot.id"
                  :color="getChipColor(slot)"
                  :text-color="getChipTextColor(slot)"
                  small
                  label
                  class="chip-slot mr-1 mb-1"
                  @click="chipClick(slot, prof)"
                >
                  {{ slot.hora ? slot.hora.substring(0, 5) : '' }}
                </v-chip>
              </div>
            </div>

            <!-- Sin agenda ese día -->
            <div
              v-if="profesionalesConTurnosElDia(dia.diaIdx).length === 0"
              class="text-center pa-3"
            >
              <v-icon small color="grey lighten-1">mdi-calendar-remove</v-icon>
              <span class="text-caption grey--text ml-1">Sin agenda este día</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialogs -->
    <NuevoTurno
      :visible="showNuevoTurno"
      :dia="turnoEdit.dia"
      :hora="turnoEdit.hora"
      :idProfesional="turnoEdit.idProfesional"
      :tiempoTurno="turnoEdit.tiempoTurno"
      :entreTurno="turnoEdit.entreTurno"
      :tipoTurno="turnoEdit.tipoTurno"
      @close="showNuevoTurno = false"
      @turnoRegistrado="turnoRegistrado"
    ></NuevoTurno>
  </div>
</template>

<script>
import axios from "../../axios-auth.js";
import { puedeFinalizarAtencion } from "../../utils/utils.js";
import NuevoTurno from "./NuevoTurno.vue";

export default {
  name: "TurnosPorServicio",
  components: { NuevoTurno },
  data() {
    return {
      servicioSeleccionado: null,
      servicios: [],
      profesionalesServicio: [],
      turnosData: {},
      fechaInicioSemana: null,
      cargando: false,
      showNuevoTurno: false,
      turnoEdit: {
        dia: "",
        hora: "",
        idProfesional: 0,
        tiempoTurno: 0,
        entreTurno: 0,
        tipoTurno: 0,
      },
    };
  },
  computed: {
    diasSemana() {
      if (!this.fechaInicioSemana) return [];
      const dias = [];
      const nombresCortos = ["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"];
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      for (let i = 0; i < 7; i++) {
        const d = new Date(this.fechaInicioSemana);
        d.setDate(d.getDate() + i);
        dias.push({
          nombreCorto: nombresCortos[i],
          fechaStr: `${d.getDate()}/${d.getMonth() + 1}`,
          fecha: new Date(d),
          diaIdx: i,
          esPasado: d < hoy,
        });
      }
      return dias;
    },
    rangoSemana() {
      if (!this.fechaInicioSemana) return "";
      const inicio = new Date(this.fechaInicioSemana);
      const fin = new Date(this.fechaInicioSemana);
      fin.setDate(fin.getDate() + 6);
      return `${inicio.getDate()}/${inicio.getMonth() + 1} - ${fin.getDate()}/${fin.getMonth() + 1}/${fin.getFullYear()}`;
    },
    esSemanaActual() {
      if (!this.fechaInicioSemana) return true;
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const inicioSemanaHoy = new Date(hoy);
      const day = inicioSemanaHoy.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      inicioSemanaHoy.setDate(inicioSemanaHoy.getDate() + diff);
      return this.fechaInicioSemana.getTime() <= inicioSemanaHoy.getTime();
    },
  },
  mounted() {
    this.calcularInicioSemana(new Date());
    this.cargarServicios();
  },
  methods: {
    calcularInicioSemana(fecha) {
      const d = new Date(fecha);
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + diff);
      d.setHours(0, 0, 0, 0);
      this.fechaInicioSemana = d;
    },
    esHoy(fecha) {
      const hoy = new Date();
      return fecha.getDate() === hoy.getDate() &&
        fecha.getMonth() === hoy.getMonth() &&
        fecha.getFullYear() === hoy.getFullYear();
    },
    semanaAnterior() {
      const d = new Date(this.fechaInicioSemana);
      d.setDate(d.getDate() - 7);
      this.fechaInicioSemana = d;
      if (this.servicioSeleccionado !== null) this.cargarTodosLosTurnos();
    },
    semanaSiguiente() {
      const d = new Date(this.fechaInicioSemana);
      d.setDate(d.getDate() + 7);
      this.fechaInicioSemana = d;
      if (this.servicioSeleccionado !== null) this.cargarTodosLosTurnos();
    },
    irAHoy() {
      this.calcularInicioSemana(new Date());
      if (this.servicioSeleccionado !== null) this.cargarTodosLosTurnos();
    },
    cargarServicios() {
      const profs = this.$store.state.usuario.profesionales || [];
      const serviciosMap = {};
      profs.forEach((p) => {
        const idServ = p.servicio || 0;
        const nomServ = p.nomServicio || "General";
        if (!serviciosMap[idServ]) {
          serviciosMap[idServ] = { servicio: idServ, nomServicio: nomServ };
        }
      });
      this.servicios = Object.values(serviciosMap).sort((a, b) =>
        a.nomServicio.localeCompare(b.nomServicio)
      );
    },
    cargarProfesionalesDelServicio() {
      const profs = this.$store.state.usuario.profesionales || [];
      this.profesionalesServicio = profs
        .filter((p) => (p.servicio || 0) === this.servicioSeleccionado)
        .map((p) => ({
          idProfesional: p.idProfesional,
          nombreProfesional: p.nombreProfesional,
        }))
        .sort((a, b) => a.nombreProfesional.localeCompare(b.nombreProfesional));
      this.cargarTodosLosTurnos();
    },
    async cargarTodosLosTurnos() {
      if (!this.profesionalesServicio.length) return;
      this.cargando = true;
      const fecha = this.toISOLocal(this.fechaInicioSemana);
      const idInstitucion = this.$store.state.usuario.institucion.idInstitucion;

      const promesas = this.profesionalesServicio.map((prof) =>
        axios
          .get("/Turnos/ArmarTurnosWideOptimizado", {
            params: {
              idProfesional: prof.idProfesional,
              fecha,
              idInstitucion,
            },
          })
          .then((res) => ({ idProfesional: prof.idProfesional, data: res.data }))
          .catch(() => ({ idProfesional: prof.idProfesional, data: [[], [], [], [], [], [], []] }))
      );

      const resultados = await Promise.all(promesas);
      const nuevoData = {};
      resultados.forEach((r) => {
        nuevoData[r.idProfesional] = r.data;
      });
      this.turnosData = nuevoData;
      this.cargando = false;
    },
    profesionalesConTurnosElDia(diaIdx) {
      return this.profesionalesServicio.filter((prof) => {
        const data = this.turnosData[prof.idProfesional];
        return data && data[diaIdx] && data[diaIdx].length > 0;
      });
    },
    getTurnosProfDia(idProfesional, diaIdx) {
      const data = this.turnosData[idProfesional];
      if (!data || !data[diaIdx]) return [];
      return data[diaIdx];
    },
    getChipColor(slot) {
      if (slot.idExcepcion) return "grey lighten-2";
      if (slot.entreTurno === 1 && slot.idTurno) return "purple lighten-4";
      if (!slot.idTurno) return "green lighten-4";
      return "red lighten-4";
    },
    getChipTextColor(slot) {
      if (slot.idExcepcion) return "grey darken-1";
      if (slot.entreTurno === 1 && slot.idTurno) return "purple darken-3";
      if (!slot.idTurno) return "green darken-3";
      return "red darken-3";
    },
    chipClick(slot, prof) {
      if (slot.idExcepcion) return; // No hacer nada en cancelados

      if (!slot.idTurno) {
        // Disponible → Nuevo Turno
        this.turnoEdit = {
          dia: slot.fechaTurnoString,
          hora: slot.hora ? slot.hora.substring(0, 5) : "",
          idProfesional: prof.idProfesional,
          tiempoTurno: slot.tiempoTurno || 0,
          entreTurno: 0,
          tipoTurno: slot.tipoTurno || 0,
        };
        this.showNuevoTurno = true;
      } else {
        // Ocupado. Este componente es "solo alta de turnos": el click sobre un
        // chip ocupado da un Entre Turno. Si el turno cumple el predicado
        // compartido (tracking habilitado, Atendido, sin fecha de fin de
        // atención) ofrecemos además "Finalizar Atención" sin romper el flujo
        // por defecto. No hay bandera `copiar` en esta grilla: modo normal (0).
        if (
          puedeFinalizarAtencion(
            this.$store.state.usuario.institucion.trackingHabilitado,
            slot.estadoTurno,
            0,
            slot.fechaFinAtencion
          )
        ) {
          this.$swal
            .fire({
              title: "Turno atendido",
              text: "¿Qué desea hacer con este turno?",
              icon: "question",
              showCancelButton: true,
              showCloseButton: true,
              background: "#ececec",
              confirmButtonText: "Finalizar Atención",
              cancelButtonText: "Dar Entre Turno",
            })
            .then((result) => {
              if (result.value === true) {
                this.finalizarAtencion(slot);
              } else if (result.dismiss === "cancel") {
                this.darEntreTurno(slot, prof);
              }
            });
          return;
        }

        this.darEntreTurno(slot, prof);
      }
    },
    darEntreTurno(slot, prof) {
      // Ocupado → Entre Turno
      {
        const hora = slot.hora ? slot.hora.substring(0, 5) : "";

        // Verificar si ya hay una cadena de entreturnos en el rango.
        const cadena = this.obtenerUltimoEntreTurno(
          prof.idProfesional,
          slot.fechaTurnoString,
          hora,
        );

        if (cadena) {
          this.$swal.fire({
            icon: "warning",
            title: "Ya hay entreturnos en este horario",
            html:
              "El último entreturno dado en este rango es el de las <b>" +
              cadena.ultimaHora +
              "</b>.<br><br>Para agregar otro, dé el entreturno sobre el entreturno de las <b>" +
              cadena.ultimaHora +
              "</b> (el nuevo quedará a las " +
              cadena.proximaHoraLibre +
              ") en lugar del turno original.",
            background: "#ececec",
            confirmButtonText: "Entendido",
          });
          return;
        }

        const parts = hora.split(":");
        let h = parseInt(parts[0]);
        let m = parseInt(parts[1]) - 1;
        if (m < 0) { m = 59; h--; }
        const horaET = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        this.turnoEdit = {
          dia: slot.fechaTurnoString,
          hora: horaET,
          idProfesional: prof.idProfesional,
          tiempoTurno: slot.tiempoTurno || 0,
          entreTurno: 1,
          tipoTurno: slot.tipoTurno || 0,
        };
        this.showNuevoTurno = true;
      }
    },
    finalizarAtencion(item) {
      let self = this;
      this.$swal
        .fire({
          title: "Finalizar Atención",
          text: "¿Desea marcar la atención de este turno como finalizada?",
          icon: "question",
          showCancelButton: true,
          showCloseButton: true,
          background: "#ececec",
          confirmButtonText: "Si",
          cancelButtonText: "No",
          showLoaderOnConfirm: true,
        })
        .then((result) => {
          if (result.value === true) {
            axios
              .get("/Turnos/FinalizarAtencion?", {
                params: {
                  idTurno: item.idTurno,
                },
              })
              .then(() => {
                self.$swal.fire({
                  icon: "success",
                  title: "Atención finalizada",
                  background: "#ececec",
                  timer: 1500,
                  showConfirmButton: false,
                });
                self.cargarTodosLosTurnos();
              })
              .catch(() => {
                self.$swal.fire({
                  icon: "error",
                  title: "No se pudo finalizar la atención",
                  background: "#ececec",
                });
              });
          }
        });
    },
    horaAMinutos(hora) {
      if (!hora) return null;
      const partes = hora.toString().substring(0, 5).split(":");
      if (partes.length < 2) return null;
      return parseInt(partes[0], 10) * 60 + parseInt(partes[1], 10);
    },
    minutosAHora(min) {
      const h = Math.floor(min / 60);
      const m = min % 60;
      return (h < 10 ? "0" + h : "" + h) + ":" + (m < 10 ? "0" + m : "" + m);
    },
    // Busca la cadena de entreturnos (minutos consecutivos hacia atrás) para
    // un profesional en un día, y devuelve el último ocupado y la próxima hora libre.
    obtenerUltimoEntreTurno(idProfesional, fechaTurnoString, horaBaseTurno) {
      const data = this.turnosData[idProfesional] || [];
      // Aplanar todos los slots del profesional y filtrar por la fecha del turno
      const slotsDelDia = []
        .concat(...data)
        .filter((s) => s && s.fechaTurnoString === fechaTurnoString);

      const baseMin = this.horaAMinutos(horaBaseTurno);
      if (baseMin === null) return null;

      const ocupados = new Set(
        slotsDelDia
          .filter((s) => s.idTurno) // solo slots con turno realmente ocupado
          .map((s) => this.horaAMinutos(s.hora))
          .filter((m) => m !== null),
      );

      let candidato = baseMin - 1;
      let ultimoOcupado = null;
      while (ocupados.has(candidato)) {
        ultimoOcupado = candidato;
        candidato -= 1;
      }

      if (ultimoOcupado === null) return null;
      return {
        ultimaHora: this.minutosAHora(ultimoOcupado),
        proximaHoraLibre: this.minutosAHora(ultimoOcupado - 1),
      };
    },
    turnoRegistrado() {
      this.showNuevoTurno = false;
      this.cargarTodosLosTurnos();
    },
    toISOLocal(d) {
      const z = (n) => ("0" + n).slice(-2);
      return d.getFullYear() + "-" + z(d.getMonth() + 1) + "-" + z(d.getDate());
    },
  },
};
</script>

<style scoped>
.dia-card {
  transition: all 0.2s;
  height: 100%;
}

.dia-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.dia-pasado {
  opacity: 0.35;
  min-height: 60px;
}

.dia-hoy {
  border: 2px solid #1976d2 !important;
}

.dia-header {
  background: #fafafa;
}

.prof-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.prof-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
}

.chip-slot {
  cursor: pointer;
  font-weight: 600;
  font-size: 0.7rem !important;
  min-width: 46px;
  justify-content: center;
}

.chip-slot:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}
</style>
